import { useQueryClient } from '@tanstack/react-query'
import { isNil, isNotNil } from 'common/checks'
import { usePostEuWithdrawal } from 'queries/post-eu-withdrawal'
import { useState } from 'react'
import styled from 'styled-components'
import { Order } from 'types/order'

import { PrimaryButton, SecondaryButton } from 'components/common/buttons'
import { Modal } from 'components/common/modal'
import { StyledParagraph } from 'components/common/text'
import { newPalette } from 'components/constants/colors'
import { WithdrawalConstants } from 'components/pages/purchases-page/constants'

// German buyers get the §356a BGB wording, every other eligible buyer the Consumer Rights
// Directive wording. English is the fallback rather than a guess: buyers outside the EU never
// reach the CTA, because eligibility is decided server-side.
const GERMAN_BILLING_COUNTRY = 'DE'

const StyledEmphasis = styled.span`
  color: ${newPalette.red[400]};
  font-weight: 500;
`

const StyledError = styled(StyledParagraph)`
  color: ${newPalette.red[400]};
  margin-top: 12px;
`

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`

function getCopy(billingCountryCode: Order['billing_country_code']) {
  // Compared case-insensitively on purpose. Nothing sends this field yet, so its casing is not
  // settled, and getting it wrong means a German buyer silently receives the English statutory
  // wording rather than the §356a BGB wording they are owed.
  if (billingCountryCode?.toUpperCase() === GERMAN_BILLING_COUNTRY) {
    return WithdrawalConstants.de
  }

  return WithdrawalConstants.en
}

export interface WithdrawalCtaProps {
  order: Order

  // Passed down rather than read via useGetToken, which would require a router in tests.
  token: string | null
}

export function WithdrawalCta({ order, token }: WithdrawalCtaProps): JSX.Element | null {
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()
  const { mutate, status, reset } = usePostEuWithdrawal()

  const isPending = status === 'loading'
  const hasSucceeded = status === 'success'
  const hasFailed = status === 'error'

  // Eligibility is the server's call, and the transaction is what the request is keyed on.
  if (order.can_request_eu_withdrawal !== true || isNil(order.last_transaction)) {
    return null
  }

  const copy = getCopy(order.billing_country_code)
  const confirmLabel = isNotNil(order.subscription_id) ? copy.ConfirmButton.subscription : copy.ConfirmButton.oneOff
  const transactionId = order.last_transaction.id

  const handleConfirm = () => {
    mutate({ transactionId, token })
  }

  const handleClose = () => {
    // Modal routes close, Escape and mask click through onClose, so this guard covers all three.
    if (isPending) {
      return
    }

    setIsOpen(false)

    if (hasSucceeded) {
      // Deferred from onSuccess: an immediate refetch unmounts this before the buyer reads it.
      queryClient.invalidateQueries(['orders'])
    }

    // Otherwise reopening the modal shows the previous outcome instead of the confirmation step.
    reset()
  }

  const footer = hasSucceeded ? (
    <PrimaryButton text={copy.DismissButton} onClick={handleClose} />
  ) : (
    <PrimaryButton text={confirmLabel} onClick={handleConfirm} isLoading={isPending} isDisabled={isPending} />
  )

  const body = hasSucceeded ? (
    <StyledParagraph>{copy.SuccessBody}</StyledParagraph>
  ) : (
    <>
      <StyledParagraph>
        {copy.ModalBody.before}
        <StyledEmphasis>{copy.ModalBody.emphasis}</StyledEmphasis>
        {copy.ModalBody.after}
      </StyledParagraph>
      {hasFailed && <StyledError role="alert">{copy.ErrorMessage}</StyledError>}
    </>
  )

  return (
    <>
      <SecondaryButton text={copy.Cta} onClick={() => setIsOpen(true)} />
      <Modal
        isOpen={isOpen}
        title={hasSucceeded ? copy.SuccessTitle : copy.ModalTitle(order.details?.line_items[0]?.product?.name)}
        onClose={handleClose}
        footer={<StyledFooter>{footer}</StyledFooter>}
      >
        {body}
      </Modal>
    </>
  )
}
